import { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import Style from "./style.module.css";
import { Loader2 } from "lucide-react";
import Select from "react-select";
import {
  FeildError,
  FeildWraper,
  FormArea,
  FormLabel,
  SelectSearch,
  TagInput,
} from "../../../../../../component/form/form-layout";
import XCloseIcon from "../../../../../../component/icons/x-close";
import LeftSidePopupLayout from "../../../../../../component/popup/modal-layout";
import { useApiCall } from "../../../../../../component/custom-hooks/api-call";
import InnerLoader from "../../../../../../component/ui/loader/inner-loader";

const AddAccountManager = ({
  onSubmit = () => {},
  onClose = () => {},
  onError = () => {},
  agentData = {},
  brands = [],
  teams = [],
  users = [],
  tierList = [],
}) => {
  const apiCall = useApiCall();

  // DEFAULT DATA SET...
  useEffect(() => {
    if (agentData?.userid) {
      getAgentData(agentData?.userid);
    }
  }, []);

  // location lists + loading
  const [countryList, setCountryList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [loadingCountry, setLoadingCountry] = useState(false);
  const [loadingState, setLoadingState] = useState(false);
  const [loadingCity, setLoadingCity] = useState(false);

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);

  // fetch countries
  const getCountries = async () => {
    try {
      setLoadingCountry(true);
      const res = await apiCall({
        method: "get",
        url: "location/get-countries",
      });
      setCountryList(res?.data || []);
    } catch (err) {
      console.warn("Country fetch error:", err);
    } finally {
      setLoadingCountry(false);
    }
  };

  const getStates = async (countryId) => {
    if (!countryId) return;
    try {
      setLoadingState(true);
      const res = await apiCall({
        method: "get",
        url: `location/get-states-by-country/${countryId}`,
      });
      setStateList(res?.data || []);
      setCityList([]);
    } catch (err) {
      console.warn("State fetch error:", err);
    } finally {
      setLoadingState(false);
    }
  };

  const getCities = async (stateId) => {
    if (!stateId) return;
    try {
      setLoadingCity(true);
      const res = await apiCall({
        method: "get",
        url: `location/get-cities-by-state/${stateId}`,
      });
      setCityList(res?.data || []);
    } catch (err) {
      console.warn("City fetch error:", err);
    } finally {
      setLoadingCity(false);
    }
  };

  // initial load countries
  useEffect(() => {
    getCountries();
  }, []);

  // --- Auto load states/cities when initial data exists ---
  useEffect(() => {
    // once countries list available and formik? has a ?country name, pick corresponding country id and load states
    if (countryList.length && formik?.values?.country) {
      const matched = countryList.find(
        (c) => c.country_name === formik?.values?.country,
      );
      if (matched) {
        setSelectedCountry(matched.id);
        getStates(matched.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryList]);

  useEffect(() => {
    // when stateList arrives and formik? has st?ate, find and load cities
    if (stateList.length && formik?.values?.state) {
      const matched = stateList.find((s) => s.name === formik?.values?.state);
      if (matched) {
        setSelectedState(matched.id);
        getCities(matched.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateList]);

  // helpers to map options
  const countryOptions = countryList.map((c) => ({
    value: c.id,
    label: c.country_name,
  }));
  const stateOptions = stateList.map((s) => ({ value: s.id, label: s.name }));
  const cityOptions = cityList.map((ct) => ({ value: ct.id, label: ct.name }));

  const teamsOptions = teams?.map((tm) => ({
    value: tm?.id,
    label: tm?.team_name + ` (${tm?.ttype})`,
  }));
  const usersOptions = users?.map((tm) => ({
    value: tm?.usertypeid,
    label: tm?.usertype,
  }));
  const brandsOptions = brands?.map((tm) => ({
    value: tm?.brandsid,
    label: tm?.brandname,
  }));
  const tierOptions = tierList
    ?.filter((fl) => fl?.status === "1")
    ?.map((tm) => ({
      value: tm?.id,
      label: tm?.name,
    }));

  const validationSchema = yup.object({
    name: yup
      .string("This Field is Required")
      .required("This Field is Required"),
    tier: yup
      .string("This Field is Required")
      .required("This Field is Required"),
    email: yup
      .string("This Field is Required")
      .required("This Field is Required"),
    phone: yup
      .string()
      .required("Phone number is required")
      .matches(/^\+?[0-9\s\-\(\)]+$/, "Invalid phone number format"),
    // usertype: yup
    //   .string("This Field is Required")
    //   .required("This Field is Required"),
    country: yup.string("This Field is Required").nullable(),
    state: yup.string("This Field is Required").nullable(),
    city: yup.string("This Field is Required").nullable(),
    brand_id: yup
      .array()
      .of(yup.string())
      .min(1, "This Field is Required")
      .required("This Field is Required"),
    team_id: yup
      .array()
      .of(yup.string())
      .min(1, "This Field is Required")
      .required("This Field is Required"),
    specialization: yup
      .array("This Field is Required")
      .of(yup.string().trim())
      .min(1, "At least one tag is required")
      .required("At least one tag is required"),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phone: "",
      usertype: "",
      country: "",
      state: "",
      city: "",
      brand_id: "",
      team_id: "",
      specialization: "",
      tier: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      let data = {
        name: values.name,
        email: values.email,
        phone: values.phone,
        usertype: 2,
        country: values.country,
        state: values.state,
        city: values.city,
        tier: values.tier,
        brand_id: values.brand_id.map((id) => Number(id)),
        team_id: values.team_id.map((id) => Number(id)),
        specialization: Array.isArray(values.specialization)
          ? values.specialization.join(",")
          : values.specialization,
      };

      console.log("Form data", data);

      try {
        let apiRes = await apiCall({
          method: "post",
          url: agentData?.userid
            ? `users/update-agent/${agentData?.userid}`
            : `users/add-agent`,
          data: data,
        });
        formik.resetForm();
        onSubmit(apiRes?.message || `Request Submitted`);
        onClose();
      } catch (error) {
        console.log("Error", error);
        const res = error?.response?.data;

        if (res?.errors) {
          formik.setErrors(
            Object.fromEntries(
              Object.entries(res.errors).map(([key, value]) => [key, value[0]]),
            ),
          );
        }
        onError(
          error?.response.data?.respMsg ||
            error?.response.data?.responseMessage ||
            error?.response.data?.message ||
            "Some Thing Went Wrong",
        );
      }
    },
  });

  const [updateLoading, setUpdateLoading] = useState(false);
  const getAgentData = async (id) => {
    setUpdateLoading(true);
    try {
      const res = await apiCall({
        method: "get",
        url: `users/agents-edit-settings/${id}`,
      });
      // ?usertype=2

      let newAgentData = res?.data;

      formik.setFieldValue("name", newAgentData?.name);
      formik.setFieldValue("email", newAgentData?.email);
      formik.setFieldValue("phone", newAgentData?.phone);
      formik.setFieldValue("usertype", newAgentData?.usertype);
      formik.setFieldValue("country", newAgentData?.country);
      formik.setFieldValue("state", newAgentData?.state);
      formik.setFieldValue("city", newAgentData?.city);
      formik.setFieldValue("tier", Number(newAgentData?.tier));
      formik.setFieldValue(
        "specialization",
        newAgentData?.specialization?.split(",").filter(Boolean),
      );
      // formik.setFieldValue("team_id", newAgentData?.team_info?.team_id);
      formik.setFieldValue(
        "brand_id",
        Array.isArray(newAgentData?.brands)
          ? newAgentData?.brands.map((brand) => brand)
          : [],
      );
      formik.setFieldValue(
        "team_id",
        Array.isArray(newAgentData?.teams)
          ? newAgentData?.teams.map((team) => team)
          : [],
      );
    } catch (error) {
    } finally {
      setUpdateLoading(false);
    }
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <LeftSidePopupLayout
        popupStatus={true}
        className={`${Style.addMeModal} ${Style.leaveModal} !gap-0`}
      >
        <div
          className={`${Style["modal_header"]} ${Style.addMeModal_head} !flex !justify-between !items-center`}
        >
          <h4>{agentData?.userid ? "Update" : "Add"} Account Manager</h4>
          <div
            onClick={onClose}
            className={`${Style["close_btn"]} cursor-pointer hover:text-red-500 p-1 rounded hover:bg-gray-200 transition-all duration-300`}
          >
            <XCloseIcon />
          </div>
        </div>

        <div className={`${Style.addMeModal_body}`}>
          {updateLoading ? (
            <div className="flex justify-center items-center !h-full w-full">
              <InnerLoader />
            </div>
          ) : (
            <FormArea>
              {/* Name */}
              <FeildWraper>
                <FormLabel required>Name</FormLabel>
                <input
                  type="text"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="name"
                />
                {formik.touched.name && formik.errors.name && (
                  <FeildError>{formik.errors.name}</FeildError>
                )}
              </FeildWraper>

              {/* Email */}
              <FeildWraper>
                <FormLabel required>Email</FormLabel>
                <input
                  type="text"
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="email"
                />
                {formik.touched.email && formik.errors.email && (
                  <FeildError>{formik.errors.email}</FeildError>
                )}
              </FeildWraper>

              {/* Phone */}
              <FeildWraper>
                <FormLabel required>Phone</FormLabel>
                <input
                  type="tel"
                  name="phone"
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Phone"
                />
                {formik.touched.phone && formik.errors.phone && (
                  <FeildError>{formik.errors.phone}</FeildError>
                )}
              </FeildWraper>

              {/* User Type */}
              {/* <FeildWraper>
                <FormLabel required>User Type</FormLabel>

                <SelectSearch
                  name="usertype"
                  placeholder="- Select User Type -"
                  onChange={(val) => {
                    formik?.setFieldValue("usertype", val);
                  }}
                  value={formik?.values.usertype}
                  options={usersOptions}
                  onBlur={() => formik.setFieldTouched("usertype", true, true)}
                  menuPosition="fixed"
                />
                {formik.touched.usertype && formik.errors.usertype && (
                  <FeildError>{formik.errors.usertype}</FeildError>
                )}
              </FeildWraper> */}

              {/* Tier */}
              <FeildWraper>
                <FormLabel required>Tier</FormLabel>

                <SelectSearch
                  name="tier"
                  placeholder="- Select Tier -"
                  onChange={(val) => {
                    formik?.setFieldValue("tier", val || "");
                  }}
                  value={formik.values.tier}
                  options={tierOptions}
                  onBlur={() => formik.setFieldTouched("tier", true, true)}
                  // menuPosition="fixed"
                />
                {formik.touched.tier && formik.errors.tier && (
                  <FeildError>{formik.errors.tier}</FeildError>
                )}
              </FeildWraper>

              {/* Brand ID */}
              <FeildWraper>
                <FormLabel required>Brand</FormLabel>
                <SelectSearch
                  name="brand_id"
                  multiple
                  placeholder="- Select Brands -"
                  onChange={(val) => {
                    formik.setFieldValue("brand_id", val);
                  }}
                  value={formik.values.brand_id}
                  options={brandsOptions}
                  onBlur={() => formik.setFieldTouched("brand_id", true, true)}
                  // menuPosition="fixed"
                />
                {formik.touched.brand_id && formik.errors.brand_id && (
                  <FeildError>{formik.errors.brand_id}</FeildError>
                )}
              </FeildWraper>

              {/* Team ID*/}
              <FeildWraper>
                <FormLabel required>Team</FormLabel>

                <SelectSearch
                  name="team_id"
                  multiple
                  placeholder="- Select Teams -"
                  onChange={(val) => {
                    formik.setFieldValue("team_id", val);
                  }}
                  value={formik.values.team_id}
                  options={teamsOptions}
                  onBlur={() => formik.setFieldTouched("team_id", true, true)}
                  // menuPosition="fixed"
                />

                {formik.touched.team_id && formik.errors.team_id && (
                  <FeildError>{formik.errors.team_id}</FeildError>
                )}
              </FeildWraper>

              {/* specialization */}
              <FeildWraper>
                <FormLabel required>Specialization</FormLabel>
                <TagInput
                  value={formik.values.specialization}
                  placeholder="Press enter to create a tag"
                  onChange={(val) => {
                    formik.setFieldTouched("specialization", true);
                    formik.setFieldValue("specialization", val, true);
                  }}
                />

                {
                  formik.touched.specialization &&
                  formik.errors.specialization ? (
                    <FeildError>{formik.errors.specialization}</FeildError>
                  ) : null
                  // <FeildError className="!text-[#3b82f6]">
                  //   Note: Press enter to create a tag
                  // </FeildError>
                }
              </FeildWraper>

              {/* Country */}
              <FeildWraper>
                <FormLabel required>Country</FormLabel>

                <SelectSearch
                  name="country"
                  placeholder="- Select Country -"
                  onChange={(val) => {
                    getStates(val);
                    formik.setFieldValue("country", val);
                    formik?.setFieldValue("state", "");
                    formik?.setFieldValue("city", "");
                    setSelectedCountry(val);
                    setSelectedState(null);
                  }}
                  value={formik.values.country}
                  options={countryOptions}
                  onBlur={() => formik.setFieldTouched("country", true, true)}
                  // menuPosition="fixed"
                />
                {formik.touched.country && formik.errors.country && (
                  <FeildError>{formik.errors.country}</FeildError>
                )}
              </FeildWraper>

              {/* State */}
              <FeildWraper>
                <FormLabel required>State</FormLabel>

                <SelectSearch
                  name="state"
                  placeholder="- Select State -"
                  onChange={(val) => {
                    formik?.setFieldValue("state", val || "");
                    formik?.setFieldValue("city", "");
                    setSelectedState(val);
                    getCities(val);
                  }}
                  isDisabled={!selectedCountry}
                  value={formik.values.state}
                  options={stateOptions}
                  onBlur={() => formik.setFieldTouched("state", true, true)}
                  // menuPosition="fixed"
                />
                {formik.touched.state && formik.errors.state && (
                  <FeildError>{formik.errors.state}</FeildError>
                )}
              </FeildWraper>

              {/* City */}
              <FeildWraper>
                <FormLabel required>City</FormLabel>

                <SelectSearch
                  name="city"
                  placeholder="- Select State -"
                  onChange={(val) => {
                    formik?.setFieldValue("city", val || "");
                  }}
                  isDisabled={!selectedState}
                  value={formik.values.city}
                  options={cityOptions}
                  onBlur={() => formik.setFieldTouched("city", true, true)}
                  // menuPosition="fixed"
                />
                {formik.touched.city && formik.errors.city && (
                  <FeildError>{formik.errors.city}</FeildError>
                )}
              </FeildWraper>
            </FormArea>
          )}
          {/* <div className={`${Style.dcmb__card}`}>
            <div className={`${Style.dcmb__cardBody}`}>
              <div className={`${Style.dcmb__cardBody_formArea}  pt-3`}></div>
            </div>
          </div> */}
        </div>

        <div className={`${Style.dcmb__cardFoot}`}>
          <button
            type="reset"
            className={`${Style.dcmb__cancel}`}
            onClick={onClose}
          >
            Discard
          </button>
          <button type="submit" className={Style.dcmb__submit}>
            {agentData?.userid ? "Update" : "Add"} Account Manager
          </button>
        </div>
      </LeftSidePopupLayout>
    </form>
  );
};

export default AddAccountManager;
